import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { Jsona } from "jsona";
import type { IUserLogged } from "../interfaces/IUserLogged";
import {
  VITE_API_ENDPOINT,
  VITE_CLIENT_ID,
  VITE_CLIENT_PASSWORD,
} from "../config/constants";
import type { ICustomer } from "../models/Customer";
import type { ResetPassword } from "../interfaces/ResetPassword";
import Customer from "../models/Customer";
import { store } from "../store";
import type UserRegister from "../interfaces/UserRegister";
import type ILogin from "../interfaces/ILogin";

const dataFormatter = new Jsona();

export class AuthService {
  async register(user: UserRegister) {
    try {
      const response = await axios.post(`/eshop/register`, user);

      if (response.data) {
        const userData: IUserLogged = dataFormatter.deserialize(
          response.data,
        ) as IUserLogged;
        store.commit("auth/setAccessToken", userData.token);
        store.commit("auth/setCustomer", userData);
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      } else {
        console.error("Error desconocido durante el registro", error);
        throw new Error("Ocurrió un error durante el registro.");
      }
    }
  }

  async login(user: ILogin) {
    const productsInAssembly =
      store.getters["assembly/getFormatedProductsToLogin"];
    const assemblyConfig = store.getters["assembly/getSelectedAssemblyConfig"];

    // const productsInCart = localStorage.getItem("productsId");
    // const parsedProducts = productsInCart ? JSON.parse(productsInCart) : [];
    const parsedProducts = store.getters["cart/getLocalProductInCart"];
    const deviceToken = localStorage.getItem("device_token");
    const data = {
      grant_type: "password",
      client_id: VITE_CLIENT_ID,
      client_secret: VITE_CLIENT_PASSWORD,
      username: user.email,
      password: user.password,
      scope: "",
      shopping_cart: parsedProducts,
      assembly_products: productsInAssembly,
      assmebly_name: assemblyConfig.name,
      device_token: deviceToken,
    };
    const options: AxiosRequestConfig = {
      baseURL: VITE_API_ENDPOINT,
      headers: {
        "Access-Control-Allow-Origin": "*",
        Accept: "application/json",
      },
    };

    try {
      const response = await axios.post(`/eshop/login`, data, options);
      const userData: IUserLogged = dataFormatter.deserialize(
        response.data,
      ) as IUserLogged;
      if (userData.token) {
        store.commit("auth/setAccessToken", userData.token);
        store.commit("auth/setCustomer", userData);
        store.commit("assembly/resetNewAssemblyConfig");
        store.commit("cart/clearLocalStorageProducts");

        // localStorage.removeItem("products");
        // localStorage.removeItem("meta");
        // localStorage.removeItem("productsId");
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      } else {
        console.error("Error desconocido", error);
        throw new Error("Ocurrió un error al intentar iniciar sesión.");
      }
    }
  }

  public getUser(): ICustomer | undefined {
    return store.getters["auth/getCustomer"];
  }

  public isUserLogged() {
    return !!this.getUser();
  }

  async session() {
    if (store.getters.getToken) {
      const response = await axios.get(`/eshop/session`);
      return response.data;
    }
  }

  public clearSessionStorage() {
    localStorage.removeItem("token");
    store.commit("auth/setAccessToken", null);
    store.commit("auth/setCustomer", null);
  }

  public async logout() {
    let success = false;
    try {
      await axios.post(`/eshop/logout`);
      store.commit("cart/resetCart");
      store.dispatch("checkout/resetCheckout", true);
      success = true;
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err?.response?.status == 401) {
          success = true;
        }
      }
      throw err;
    } finally {
      if (success) {
        this.clearSessionStorage();
        window.location.href = "/";
      }
    }
  }

  public async getAutenticatedCustomer(forceRemoteInfo: boolean = false) {
    const customer = store.getters["auth/getCustomer"];
    if (customer && !forceRemoteInfo) {
      return customer;
    }
    try {
      const response = await axios.get(`eshop/autenticated-customer`);
      if (response.status == 200) {
        const data = dataFormatter.deserialize(response.data);
        store.commit("auth/setCustomer", data);
        return data;
      } else {
        store.commit("auth/setCustomer", null);
        return [];
      }
    } catch (error) {
      store.commit("auth/setCustomer", null);
      console.error("Error en la solicitud del cliente autenticado:", error); // Maneja el error
      return error;
    }
  }

  public async getLoginInitData() {
    const translations = store.getters["translations/getSection"]("login");
    if (translations) {
      return translations;
    }
    const response = await axios.get(`/eshop/login/init-data`);
    if (response) {
      if (
        response.data &&
        response.data.meta &&
        response.data.meta.translations
      ) {
        store.commit("translations/setSection", {
          login: response.data.meta.translations,
        });
      }
      return response.data;
    }
  }

  public async getRegisterInitData() {
    const translations = store.getters["translations/getSection"]("register");
    if (translations) {
      return translations;
    }
    const response = await axios.get(`eshop/register/init-data`);
    if (response) {
      if (
        response.data &&
        response.data.meta &&
        response.data.meta.translations
      ) {
        store.commit("translations/setSection", {
          register: response.data.meta.translations,
        });
      }
      return response.data;
    }
  }

  async sendPasswordRecoveryLink(email: string) {
    const options: AxiosRequestConfig = {
      baseURL: VITE_API_ENDPOINT,
      headers: {
        Accept: "application/vnd.api+json",
      },
    };
    const url = "/password/send-email";
    const response = await axios.post(url, { email }, options);
    return response;
    // if (!(response instanceof AxiosError)) {
    //   const data = dataFormatter.deserialize(response.data);
    //   return data;
    // } else if (response) {
    //   return response;
    // } else {
    //   return null;
    // }
  }

  async resetPassword(data: ResetPassword) {
    const url = "/password/reset";
    const response = await axios.post(url, data);
    return response;
  }

  public getToken(): string | null {
    const token: string | null = store.getters["auth/getAccessToken"];
    if (token) {
      return token;
    }
    return null;
  }

  public async self(): Promise<Customer> {
    const res = await axios.get("/shop-v1/customers/-actions/self");
    const customer = new Customer(res.data);
    return customer;
  }

  public async isCustomerTokenExpired(): Promise<boolean> {
    if (this.isUserLogged()) {
      try {
        return !(await this.self());
      } catch (err) {
        if (err instanceof AxiosError) {
          if (err.response) {
            return err.response.status === 401;
          }
        }
      }
    }
    return false;
  }

  public async getStatusAccount() {
    const customer = store.getters["auth/getCustomer"];
    if (customer) {
      try {
        const response = await axios.get("/email/notice-unverified");
        if (response.status === 200) {
          return response.data;
        }
      } catch (error) {
        console.error("Correo no verificado todavía:", error);
      }
      return false;
    }
  }
}

const auth = new AuthService();

export default auth;
